import { useContext, useMemo, useState } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  makeStyles,
  useTheme,
} from '@material-ui/core';

import { ChatMessageReaction as ChatMessageReactionComponent } from './ChatMessageReaction';
import { MAIN_CONTAINER_ID } from '../../constants';
import { DeviceContext } from '../../contexts';
import { ChatMessage, ChatMessageReaction as ChatMessageReactionType } from '../../types';
import ChatAvatar from './ChatAvatar';

export const ChatMessageReactionsDialog = ({
  open,
  handleClose,
  message,
  loading,
  reactions,
}: {
  open: boolean;
  handleClose: () => void;
  message: ChatMessage | null;
  loading: boolean;
  reactions: ChatMessageReactionType[];
}) => {
  const { isMobile } = useContext(DeviceContext);
  const theme = useTheme();
  const classes = useStyles();
  const [selectedReaction, setSelectedReaction] = useState<ChatMessageReactionType | null>(null);

  const usersWithReactions = useMemo(() => {
    return reactions
      .filter(reaction => reaction.emoji === selectedReaction?.emoji || !selectedReaction)
      .flatMap(reaction =>
        (reaction.users || []).map(user => ({
          ...user,
          emoji: reaction.emoji,
        }))
      );
  }, [reactions, selectedReaction]);

  return (
    <Dialog
      container={document.getElementById(MAIN_CONTAINER_ID)}
      fullScreen={isMobile}
      maxWidth="xs"
      fullWidth
      open={open}
      onClose={handleClose}
    >
      <DialogTitle>Reactions</DialogTitle>

      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" height={200}>
            <CircularProgress />
          </Box>
        )}

        {!loading && (
          <Box>
            <Box
              display="flex"
              gridGap={6}
              alignItems="center"
              overflow="auto"
              marginBottom={2}
              paddingBottom={1}
              borderBottom={`2px solid ${theme.palette.divider}`}
              paddingTop={2}
            >
              <Box
                onClick={() => setSelectedReaction(null)}
                className={classes.allReactions}
                style={{
                  backgroundColor: !selectedReaction ? theme.palette.background.default : 'transparent',
                  outline: !selectedReaction ? `1px solid ${theme.palette.divider}` : 'none',
                }}
              >
                All
              </Box>
              {reactions.map(reaction => (
                <ChatMessageReactionComponent
                  key={reaction.emoji}
                  emoji={reaction.emoji}
                  onPress={() => setSelectedReaction(reaction)}
                  selected={selectedReaction?.emoji === reaction.emoji}
                />
              ))}
            </Box>

            {usersWithReactions.length > 0 && (
              <Box maxHeight={300} overflow="auto">
                {usersWithReactions.map(user => (
                  <Box
                    key={user.uid + user.emoji}
                    display="flex"
                    justifyContent="space-between"
                    marginBottom={1}
                  >
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <ChatAvatar
                        avatarUrl={user.avatarUrl}
                        name={user.name}
                        size={32}
                        style={{ marginRight: 8 }}
                      />
                      <Box>{user.name}</Box>
                    </Box>

                    <Box style={{ fontSize: 24, marginRight: 8 }}>{user.emoji}</Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles(theme => ({
  allReactions: {
    fontSize: 14,
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
    padding: '4px 12px',
    cursor: 'pointer',
    width: 'fit-content',
  },
}));
